/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import ContentsTable from '@base/ContentsTable/ContentsTable';
import MenusNav from '@base/MenusNav/MenusNav';
import { menus } from '@utils/constants';
import { Fonts, Spacings } from '@styles/index';
import Link from '@base/Link/Link';
import SEO from '@seo/index';
import Layout from '@layout/index';
import LeftContainer from '@cont/CompanyDetails/LeftContainer';
import RightContainer from '@cont/CompanyDetails/RightContainer';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const PrivacyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 24;
`;

const StyledH3 = styled(H3)`
  margin-bottom: ${Spacings.SPACING_6B};
  padding-top: 100px;
  margin-top: -100px;
`;

const StyledAnchor = styled.a`
  color: var(--color-i-blue);
  margin-left: ${Spacings.SPACING_1B};
`;

const StyledLink = styled(Link)`
  font-size: ${Fonts.SMALL};
  color: var(--color-i-blue);
`;

const tableData = [
  { label: 'A. General Privacy Terms ', id: 'general' },
  { label: 'B. Applicability', id: 'applicability' },
  { label: 'C. Access', id: 'access' },
  {
    label: 'D. Use of the Platform/Services by Children ',
    id: 'use_of_platform'
  },
  {
    label: 'E. Controllers',
    id: 'controllers'
  },
  { label: 'F. Personal Information', id: 'personal_information' },
  { label: 'G. Information We Collect', id: 'info_collect' },
  {
    label: 'H. Basis of Collection and Processing of Your Personal Information',
    id: 'basis_of_collection'
  },
  {
    label: 'I. How we Use and Share the Information Collected',
    id: 'use_share_info'
  },
  { label: 'J. Cross-Border Data Transfer', id: 'cross_border_data' },
  {
    label: 'K. Duration for which your Information is Stored',
    id: 'duration_info'
  },
  { label: 'L. Your Choices', id: 'your_choice' },
  {
    label: 'M. Your Rights',
    id: 'your_rights'
  },
  { label: 'N. Information Security', id: 'info_sec' },
  { label: 'O. Promotional Communications', id: 'personal_comm' },
  { label: 'P. Interest-Based Ads', id: 'interest_based_ads' },
  {
    label: 'Q. Modification to Privacy Policy',
    id: 'mod-privacy-policy'
  },
  {
    label: 'R. Privacy Grievances',
    id: 'grievances'
  },
  {
    label: 'S. Country Specific Additional Privacy Terms',
    id: 'country_specific_privacy_terms'
  },
  {
    label: 'T. CCPA NOTICE AT COLLECTION',
    id: 'ccpa_notice_collection'
  },
  {
    label: 'U. CCPA DATA PRACTICES DURING THE LAST 12 MONTHS',
    id: 'ccpa_data_practices'
  },
  {
    label: 'V. CONSUMER RIGHTS AND REQUESTS UNDER THE CCPA',
    id: 'consumer_rights_under_ccpa'
  }
];

const title = 'Privacy Policy | Unacademy';
const url = 'https://unacademy.com/privacy';
const description = 'Unacademy Privacy Policy';

const Privacy = () => {
  const router = useRouter();
  const { pathname, query } = router;

  return (
    <>
      <SEO
        title={title}
        description={description}
        url={url}
        openGraph={{ title, description, url }}
      />
      <LeftContainer>
        <MenusNav data={menus} currentHref={pathname} />
      </LeftContainer>

      <RightContainer>
        <PrivacyWrapper>
          <div>
            {query.in_app !== 'true' && <H2 as="h1">Privacy policy</H2>}
            <br />
            <div>
              <article>
                <br />
                <ContentsTable data={tableData} />
                <a name="general">
                  <StyledH3>A. General</StyledH3>
                </a>
                <P1>
                  In addition to our{' '}
                  <StyledLink href="/terms" as="/terms">
                    Terms and conditions
                  </StyledLink>{' '}
                  Service, Unacademy respects your privacy and is committed to
                  protecting it. This Privacy Policy (the “Policy”) explains the
                  types of information collected by Unacademy when you use the
                  Platform (as defined in{' '}
                  <StyledLink href="/terms" as="/terms">
                    Terms and conditions
                  </StyledLink>
                  ) that references this Policy, how we collect, use, share and
                  store such information collected and also explains the
                  rationale for collection of such information, the privacy
                  rights and choices you have regarding your information
                  submitted to us when you use the Services.
                </P1>
                <br />
                <P1>
                  For ease of reference, use of the terms “Unacademy”, “we”,
                  “us, and/or “our” refer to Sorting Hat Technologies Private
                  Limited – a company incorporated in India and all of its
                  affiliates which have license to host the Platform and offer
                  Services. Similarly, use of the terms “you”, “yours” and/or
                  “User(s)” refer to all users of the Platform and includes all
                  Learners and Content Providers{' '}
                  <i>
                    (as more particularly defined under our{' '}
                    <StyledLink href="/terms" as="/terms">
                      Terms and conditions
                    </StyledLink>
                    )
                  </i>
                  .
                </P1>
                <br />
                <P1>
                  The Services are governed by this Policy,{' '}
                  <StyledLink href="/terms" as="/terms">
                    Terms and conditions
                  </StyledLink>
                  , and any other rules, policies or guidelines published on the
                  Platform as applicable to you. Please read this Policy
                  carefully prior to accessing our Platform and using the
                  Services. By accessing and using the Platform, providing your
                  Personal Information (defined below), or by otherwise
                  signalling your agreement when the option is presented to you,
                  you consent to the collection, use, disclosure, sharing and
                  storing of information described in this Policy, Terms of
                  Service and any other rules, policies or guidelines published
                  on the Platform as applicable to you (collectively referred to
                  as the <b>“Platform Terms”</b>), and Unacademy disclaims all
                  the liabilities arising therefrom. If you have inadvertently
                  submitted any Personal Information to Unacademy prior to
                  reading this Policy statements set out herein, or you do not
                  agree with the way your Personal Information is collected,
                  stored, or used, then you may access, modify and/or delete
                  your Personal Information in accordance with this Policy
                  (refer to the sections about <b>Your Choices</b> and{' '}
                  <b>Your Rights</b>).
                </P1>
                <br />
                <P1>
                  If any information you have provided or uploaded on the
                  Platform violates the Platform Terms, Unacademy may be
                  required to delete such information upon informing you of the
                  same and revoke your access to the Platform if required.
                </P1>
                <br />
                <P1>
                  Capitalized terms used but not defined in this Policy can be
                  found in our{' '}
                  <StyledLink href="/terms" as="/terms">
                    Terms and conditions
                  </StyledLink>
                  .
                </P1>
                <br />
                <P1>
                  If you have any questions about this Policy, please contact us
                  at
                  <StyledAnchor href="mailto:privacy@unacademy.com">
                    privacy@unacademy.com
                  </StyledAnchor>
                  .
                </P1>
                <br />
                <br />
                <a name="applicability">
                  <StyledH3>B. Applicability</StyledH3>
                </a>
                <P1>This Policy applies to all Users of the Platform.</P1>
                <br />
                <P1>
                  Unacademy owns and/or manages several platforms (other than
                  the Platform) that offer a range of services including
                  services related to technology solutions in the space of
                  education, either by itself or through its affiliates and
                  subsidiaries (<b>“Unacademy Group Platforms”</b>). Each of the
                  Unacademy Group Platforms have published their own privacy
                  policies. Accordingly, this Policy does not apply to
                  information collected through the Unacademy Group Platforms
                  and only applies to the collection of your information through
                  the Platform. Please visit the relevant Unacademy Group
                  Platform to know the privacy practices undertaken by them.
                </P1>
                <br />
                <P1>
                  Unacademy has taken reasonable precautions as per applicable
                  laws and implemented industry standards to treat Personal
                  Information as confidential and to protect it from
                  unauthorized access, improper use or disclosure, modification
                  and unlawful destruction or accidental loss of the Personal
                  Information.
                </P1>
                <br />
                <br />
                <a name="access">
                  <StyledH3>C. Access</StyledH3>
                </a>
                <P1>
                  You may be allowed to access and view the Platform as a guest
                  and without creating an account on the Platform or providing
                  any Personal Information; Unacademy does not validate or takes
                  no responsibility towards information, if any, provided by you
                  as a guest, except as otherwise required under any law,
                  regulation, or an order of competent authority. However, to
                  have access to all the features and benefits on our Platform,
                  you are required to first create an account on our Platform.
                  To create an account, you are required to provide certain
                  Personal Information as may be required during the time of
                  registration and all other information requested on the
                  registration page, including the ability to receive
                  promotional offers from Unacademy, is optional. Unacademy may,
                  in future, include other optional requests for information
                  from you to help Unacademy to customize the Platform to
                  deliver personalized information to you. Unacademy may keep
                  records of telephone calls or emails received from or made by
                  you for making enquiries, feedback, or other purposes for the
                  purpose of rendering Services effectively and efficiently.
                </P1>
                <br />
                <br />
                <a name="use_of_platform">
                  <StyledH3>
                    D. Use of the Platform/Services by Children
                  </StyledH3>
                </a>
                <P1>
                  As stated in our{' '}
                  <StyledLink href="/terms" as="/terms">
                    Terms and conditions
                  </StyledLink>
                  , to register on the Platform you must meet the ‘Age
                  Requirements’ (as defined in our{' '}
                  <StyledLink href="/terms" as="/terms">
                    Terms and conditions
                  </StyledLink>
                  ). If you are a <b>“Minor”</b> or <b>“Child”</b> i.e., an
                  individual who does not meet the Age Requirements, then you
                  may not register on the Platform, and only your Parent (
                  <i>defined below</i>) can register on the Platform on your
                  behalf, agree to all Platform Terms and enable access to you
                  under their guidance and supervision.
                </P1>
                <br />
                <P1>
                  While some of our Services may require collection of a Child’s
                  Personal Information, we do not knowingly collect such
                  Personal Information from a Child and assume that information
                  has been provided with consent of the Parent. If you are a
                  Parent and you are aware that your Child has provided us with
                  any Personal Information without your consent, please contact
                  us at
                  <StyledAnchor href="mailto:privacy@unacademy.com">
                    privacy@unacademy.com
                  </StyledAnchor>
                  . If we become aware that we have collected Personal
                  Information from a Child in the absence of consent of the
                  Parent, we will take steps to remove such information from our
                  servers.
                </P1>
                <br />
                <P1>
                  The information in the relevant parts of this notice applies
                  to Children as well as adults. If your Child faces any form of
                  abuse or harassment while availing our Services, you must
                  write to us at
                  <StyledAnchor href="mailto:legal@unacademy.com">
                    legal@unacademy.com
                  </StyledAnchor>
                  , so that necessary actions could be considered.
                </P1>
                <br />
                <br />
                <a name="controllers">
                  <StyledH3>E. Controllers</StyledH3>
                </a>
                <P1>
                  Unacademy is the controller of Personal Information that it
                  collects and processes in connection with the use of the
                  Platform and the provision of the Services on the Platform.
                  The kind of Personal Information we collect in connection with
                  such use is detailed below.
                </P1>
                <br />
                <br />
                <a name="personal_information">
                  <StyledH3>F. Personal Information</StyledH3>
                </a>
                <P1>
                  <b>“Personal Information”</b> shall mean the information which
                  identifies a User i.e., first and last name, identification
                  number, email address, age, gender, location, photograph
                  and/or phone number provided at the time of registration or
                  any time thereafter on the Platform.
                </P1>
                <br />
                <P1>
                  <b>“Sensitive Personal Information”</b> shall include (i)
                  passwords and financial data (except the truncated last four
                  digits of credit/debit card), (ii) health data, (iii) official
                  identifier (such as biometric data, aadhar number, social
                  security number, driver’s license, passport, etc.,), (iv)
                  information about sexual life, sexual identifier, race,
                  ethnicity, political or religious belief or affiliation, (v)
                  account details and passwords, or (vi) other data/information
                  categorized as ‘sensitive personal data’ or ‘special
                  categories of data’ under the Information Technology
                  (Reasonable Security Practices and Procedures and Sensitive
                  Personal Data or Information) Rules, 2011, General Data
                  Protection Regulation (GDPR) and / or the California Consumer
                  Privacy Act (CCPA) (<b>“Data Protection Laws”</b>) and in
                  context of this Policy or other equivalent / similar
                  legislations.
                </P1>
                <br />
                <P1>
                  Usage of the term <b>‘Personal Information’</b> shall include{' '}
                  <b>‘Sensitive Personal Information’</b> as may be applicable
                  to the context of its usage.
                </P1>
                <br />
                <P1>
                  We request you to not provide Unacademy with any Personal
                  Information unless specifically requested by us. In the event
                  you share with Unacademy any Personal Information without us
                  having specifically requested for the same, then we bear no
                  liability in respect of such Personal Information provided by
                  you.
                </P1>
                <br />
                <br />
                <a name="info_collect">
                  <StyledH3>G. Information We Collect</StyledH3>
                </a>
                <P1>
                  We only collect information about you if we have a reason to
                  do so — for example, to provide our Services on the Platform,
                  to communicate with you, or to make our Services better.
                </P1>
                <br />
                <P1>We collect this information from the following sources:</P1>
                <br />
                <P1>
                  <li>
                    <i>Information we collect from You:</i>
                    <ol type="i">
                      <li>
                        <b>Basic account information:</b> In order to access
                        certain features of the Platform, you will need to
                        create an account and register with us. We ask for basic
                        information which may include your name, an email
                        address, state of residence country, and password, along
                        with a username and phone/mobile number.
                      </li>
                      <li>
                        <b>Know Your Customer (&#39;KYC&#39;) information:</b>{' '}
                        If you are an Content Provider registered with us, then,
                        we also collect the KYC information, which may include
                        information pertaining to your PAN number, aadhaar
                        number, driver’s license, passport, your entity details
                        such as name, MOU/AOA, certificate of incorporation,
                        list of directors/partners, social security number etc.,
                        along with the relevant documents. We collect this
                        information and documents from you to complete our
                        verification process including pursuant to any other
                        arrangement / agreement executed with you.
                      </li>
                      <li>
                        <b>Public profile information:</b> If you are an Content
                        Provider, we may also collect certain additional
                        information from you to enable creation of your public
                        profile, if any, on the Platform to help Learners and
                        other Users know you better. We do not share your KYC
                        information, documents or any such Personal Information
                        on these public profiles. Your public profile will only
                        contain Personal Information that are required for
                        Learners or Users to know you (as applicable) viz., your
                        username, a brief description of any content you have
                        made available on the Platform, your educational
                        qualifications, your past associations, your photo, and
                        such similar information that may enable a Learner or
                        other User to know you better. The information in your
                        public profile will be based on the information you
                        provide to us or as updated by you on such applicable
                        sections.
                      </li>
                      <li>
                        <b>Information when you communicate with us:</b> When
                        you write to us with a question or to ask for help, we
                        will keep that correspondence, and the email address,
                        for future reference; this may also include any phone/
                        mobile numbers if you have provided us the same as part
                        of your communication either in writing (emails
                        included), over a phone call or otherwise. When you
                        browse pages on our Platform, we will track that for
                        statistical purposes which may be to improve the
                        Platform and the Services. You may also provide us your
                        Personal Information or other information when you
                        respond to surveys, enter any form of contests, tests,
                        events, competitions, webinars, etc., hosted by
                        Unacademy, either on the Platform or otherwise, or when
                        you otherwise communicate with us via form, e-form,
                        email, phone, or otherwise, we store a copy of our
                        communications (including any call recordings or emails,
                        if any, as permitted by applicable law).
                      </li>
                      <li>
                        <b>Information related to location:</b> You may also
                        choose to provide location related information,
                        including but not limited to access to GPS, which will
                        enable us, with your consent, to offer customized
                        offerings for specific services where location data is
                        relevant and/or applicable such as informing you whether
                        services on the Platform that you have expressed
                        interest in may be availed of at or near your location.
                      </li>
                    </ol>
                  </li>
                  <br />
                  <li>
                    <i>Information we collect automatically:</i>
                    <ol type="i">
                      <li>
                        <b>Device and Log information:</b> When you access our
                        Platform, we collect information that web browsers,
                        mobile devices, and servers typically make available,
                        including the browser type, IP address, unique device
                        identifiers, language preference, referring site, the
                        date and time of access, operating system, and mobile
                        network information. We collect log information when you
                        use our Platform — for example, when you create or make
                        changes to your account information on the Platform.
                      </li>
                      <li>
                        <b>Usage information:</b> We collect information about
                        your usage of our Platform. We also collect information
                        about what happens when you use our Platform (e.g., page
                        views, support document searches, features enabled for
                        your account, interactions with other parts of our
                        Services) along with information about your Supported
                        Device (e.g., screen size, name of cellular network, and
                        mobile device manufacturer). We use this information to
                        provide our Platform to you, get insights on how people
                        use our Platform so that we can make our Platform
                        better, and understand and make predictions about User
                        retention.
                      </li>
                      <li>
                        <b>Location information:</b> We may determine the
                        approximate location of your Supported Device from your
                        Internet Protocol (IP) address. We may collect and use
                        this information to calculate how many people visit from
                        certain geographic regions or to improve our Platform
                        Services.
                      </li>
                      <li>
                        <b>Information from cookies & other technologies:</b> We
                        may collect information about you through the use of
                        cookies and other similar technologies. A cookie is a
                        string of information that a website stores on a
                        visitor’s computer, and that the visitor’s browser
                        provides to the website each time the visitor returns.
                        For more information on our use of cookies and similar
                        technologies, please refer to our{' '}
                        <StyledLink href="/cookie-policy" as="/cookie-policy">
                          Cookie Policy
                        </StyledLink>
                        .
                      </li>
                    </ol>
                  </li>
                  <br />
                  <li>
                    <i>Information we collect from other sources:</i>
                    <ul>
                      We might receive and collect information about you from
                      other sources in the course of their services to us or
                      from a publicly available sources, as permitted by law,
                      which we may combine with other information we receive
                      from or about you. For example, we may receive information
                      about you from a social media site or a Google service if
                      you connect to the Services through that site or if you
                      use the Google sign-in.
                    </ul>
                  </li>
                </P1>
                <br />
                <br />
                <a name="basis_of_collection">
                  <StyledH3>
                    H. Basis of Collection and Processing of Your Personal
                    Information
                  </StyledH3>
                </a>
                <P1>
                  <li>
                    <i>Basis for collection:</i>
                    <ul>
                      We collect and process your Personal Information based on
                      the following legal parameters depending upon the nature
                      of Personal Information and the purposes for which it is
                      processed:
                    </ul>
                    <br />
                    <ol type="i">
                      <li>
                        <b>Consent:</b> We rely on your consent to process your
                        Personal Information in certain situations. If we
                        require your consent to collect and process certain
                        Personal Information, as per the requirements under the
                        applicable Data Protection Laws, your consent is sought
                        at the time of collection of your Personal Information
                        and such processing will be performed where consent is
                        secured.
                      </li>
                      <li>
                        <b>Compliance with a legal obligation:</b> Your Personal
                        Information may be processed by us, to the extent that
                        such processing is necessary to comply with a legal
                        obligation.
                      </li>
                    </ol>
                  </li>
                </P1>
                <br />
                <P1>
                  <li>
                    <i>Processing of your Personal Information: </i>
                    <ul>
                      We may process your Personal Information in connection
                      with any of the purposes and uses set out in this Policy
                      on one or more of the following legal grounds:
                    </ul>
                    <br />
                    <ol type="i">
                      <li>
                        Because it is necessary to perform the Services you have
                        requested or to comply with your instructions or other
                        contractual obligations between you and us;
                      </li>
                      <li>
                        To comply with our legal obligations as well as to keep
                        records of our compliance processes;
                      </li>
                      <li>
                        Because our legitimate interests, or those of a
                        third-party recipient of your Personal Information, make
                        the processing necessary, provided those interests are
                        not overridden by your interests or fundamental rights
                        and freedoms;
                      </li>
                      <li>
                        Because you have chosen to publish or display your
                        Personal Information on a public area of the Platform,
                        such as a comment area;
                      </li>
                      <li>
                        Because it is necessary to protect your vital interests;
                      </li>
                      <li>
                        Because it is necessary in the public interest; or
                      </li>
                      <li>
                        Because you have expressly given us your consent to
                        process your Personal Information in a particular
                        manner.
                      </li>
                    </ol>
                  </li>
                </P1>
                <br />
                <P1>
                  We do not use Personal Information for making any automated
                  decisions affecting or creating profiles other than what is
                  described in this Policy.
                </P1>
                <br />
                <P1>
                  Where the processing of your Personal Information is based on
                  your consent, you have the right to withdraw your consent at
                  any point in time in accordance with this Policy. Please note
                  that should the withdrawal of consent result in us not being
                  able to continue offering our Services to you, we reserve the
                  right to withdraw or cease from offering our Services to you
                  upon your consent withdrawal. You may withdraw consent by
                  contacting us with a written request to the contact details
                  provided in the{' '}
                  <StyledLink href="#grievances" as="#grievances">
                    ‘Grievances’
                  </StyledLink>{' '}
                  section below. Upon receipt of your request to withdraw your
                  consent, the consequences of withdrawal may be communicated to
                  you. Upon your agreement to the same, your request for
                  withdrawal will be processed.
                </P1>
                <br />
                <br />
                <a name="use_share_info">
                  <StyledH3>
                    I. How we Use and Share the Information Collected
                  </StyledH3>
                </a>
                <P1>
                  <li>
                    We use/process your information in the following manner:
                    <ol type="i">
                      <li>
                        <b>To provide Services on our Platform:</b> We use your
                        information as collected by us to allow you to access
                        the Platform and the Services offered therein, including
                        without limitation to set-up and maintain your account,
                        provide customer service, fulfil purchases through the
                        Platform, verify User information and to resolve any
                        glitches with our Platform. The legal basis for this
                        processing is consent or, where applicable, our
                        legitimate interests in the proper administration of our
                        Platform, and/or the performance of a contract between
                        you and us.
                      </li>
                      <li>
                        <b>To improve our Platform and maintain safety:</b> We
                        use your information to improve and customize the
                        Platform and Services offered by us, including providing
                        automatic updates to newer versions of our Platform and
                        creating new features based on the Platform usage
                        analysis. Further, we also use your information to
                        prevent, detect, investigate, and take measures against
                        criminal activity, fraud, misuse of or damage to our
                        Platform or network, and other threats and violations to
                        Unacademy’s or a third party&#39;s rights and property,
                        or the safety of Unacademy, its users, or others. The
                        legal basis for this processing is consent or, where
                        applicable, our legitimate interests in the proper
                        administration of our Platform, and/or the performance
                        of a contract between you and us.
                      </li>
                      <li>
                        <b>To market our Platform and communicate with You:</b>{' '}
                        We will use your information to develop a more targeted
                        marketing of our Platform, to communicate with you about
                        our offers, new products, services or even receive your
                        feedback on the Platform. The legal basis for this
                        processing is consent or, where applicable, our
                        legitimate interests in the proper administration of our
                        Platform, and/or the performance of a contract between
                        you and us.
                      </li>
                      <li>
                        <b>To establish, exercise, or defend legal claims:</b>{' '}
                        We may process any Personal Information identified in
                        this Policy when necessary for establishing, exercising,
                        or defending legal claims, whether in court,
                        administrative, or other proceedings. The legal basis
                        for this processing is our legitimate interest in the
                        protection and assertion of our legal rights, your legal
                        rights, and the legal rights of others.
                      </li>
                      <li>
                        <b>To manage risk and obtain professional advice:</b>We
                        may process any of the Personal Information identified
                        in this Policy to manage risk or obtain professional
                        advice. The legal basis for this processing is our
                        legitimate interest in the proper protection of our
                        business and Platform
                      </li>
                      <li>
                        <b>Consent:</b>We may otherwise use your information
                        with your consent or at your direction.
                      </li>
                      <li>
                        <b>To Better Understand Our Users:</b>We may use
                        information we gather to determine which areas of the
                        Services are most frequently visited to understand how
                        to enhance the Services.
                      </li>
                    </ol>
                  </li>
                  <br />
                  <li>
                    We share the information collected as per terms of this
                    Policy only in the manner specified hereinbelow. We do not
                    sell or otherwise disclose Personal Information we collect
                    about you for monetary or other valuable consideration.
                    Further, only authorized representatives of Unacademy and on
                    a need-to-know basis use any information received from you
                    and as consented by you. In the event of any identified
                    unauthorized use or disclosure of information or upon your
                    complaint as stated under the ‘Grievances’ section below, we
                    will investigate any such complaint and take the appropriate
                    action as per the applicable Data Protection Laws.
                    <ol type="i">
                      <li>
                        <b>Affiliates and Subsidiaries:</b> We may disclose
                        information about you to our affiliates, subsidiaries
                        and other businesses under the same control and
                        ownership, and their respective officers, directors,
                        employees, accountants, attorneys, or agents, who need
                        the information to help us provide the Services or
                        process the information on our behalf. We require our
                        affiliate, subsidiaries and other businesses under the
                        same control and ownership to follow this Privacy Policy
                        for any Personal Information that we share with them.
                        Further, if you have availed any Service that is offered
                        by Unacademy in collaboration with one or more Unacademy
                        Group Platforms, then we may share your Personal
                        Information with the relevant Unacademy Group Platform,
                        who may also communicate with you regarding certain
                        other products/services offered by them separately via
                        email, phone, SMS and/or such other mode of
                        communication; you will always have an option to opt out
                        of receiving some or all of such promotional
                        communications from an Unacademy Group Platform through
                        the unsubscribe link in the email or such other option
                        that may be communicated to you.
                      </li>
                      <li>
                        <b>
                          Third-party vendors/service providers including
                          integrated services on the Platform:{' '}
                        </b>{' '}
                        We may share information about you with third-party
                        vendors or service providers (including consultants,
                        payment processors, and other service providers and
                        integrated services) who need the information to provide
                        their support services to us or you, or to provide their
                        services to you on our behalf either directly or through
                        the Platform. These services may include providing
                        customer support, performing business and sales
                        analysis, supporting our website functionality,
                        facilitating payment processing, and supporting
                        contests, surveys, and other features offered on our
                        Platform. Such third-party vendors are not allowed to
                        use the information for any purpose other than what it
                        was provided for, and they are required to process and
                        use the information in accordance with this Privacy
                        Policy.
                      </li>
                      <li>
                        <b>
                          Third-party platforms to facilitate the additional
                          offerings:
                        </b>{' '}
                        We may share information about you with third-party
                        platforms that are used to create interactive forums
                        between Content Providers and Learners to enable the
                        Learners a more conducive environment to extend their
                        learning. The information shared with these platforms
                        are limited to the information required to facilitate
                        your participation in the forums and is based on your
                        consent. However, the privacy practices of these
                        platforms are independent of this Policy, and we suggest
                        that you read through the privacy policies and terms and
                        condition of the platforms relevant to you prior to
                        consenting to being a part of any forums made available
                        on these third-party platforms.
                      </li>
                      <li>
                        <b>Legal Disclosures:</b> We may disclose information
                        about you in response to a court order, or other
                        governmental request. Without limitation to the
                        foregoing, we reserve the right to disclose such
                        information where we believe in good faith that such
                        disclosure is necessary to:
                        <ul>
                          <li>
                            comply with applicable laws, regulations, court
                            orders, government and law enforcement agencies’
                            requests;
                          </li>
                          <li>
                            protect and defend Unacademy’s or a third
                            party&#39;s rights and property, or the safety of
                            Unacademy, our users, our employees, or others; or
                          </li>
                          <li>
                            prevent, detect, investigate and take measures
                            against criminal activity, fraud and misuse or
                            unauthorized use of our Platform and/or to enforce
                            our Terms and Conditions or other agreements or
                            policies.
                          </li>
                        </ul>
                        To the extent permitted by law, we will attempt to give
                        you prior notice before disclosing your information in
                        response to such a request.
                      </li>
                      <li>
                        <b>Business transfers:</b>In the event Unacademy
                        undergoes any merger, acquisition, or sale of company
                        assets, in part or in full, with another company, or in
                        the unlikely event that Unacademy goes out of business
                        or enters bankruptcy, user information would likely be
                        one of the assets that is transferred or acquired by a
                        third party. If any of these events were to happen, this
                        Privacy Policy would continue to apply to your
                        information and the party receiving your information may
                        continue to use your information, but only consistent
                        with this Privacy Policy.
                      </li>
                      <li>
                        <b>Advertising and Analytics Partners:</b>We may share
                        usage data with third-party advertisers, advertisement
                        networks, and analytics providers through cookies and
                        other similar technologies.
                      </li>
                      <li>
                        <b>With Your Consent:</b>We may share and disclose
                        information with your consent or at your direction.
                      </li>
                    </ol>
                    <br />
                    <b>
                      Your information may be shared for reasons not described
                      in this Policy, however, we will seek your consent before
                      we do the same or share information upon your direction.
                    </b>
                  </li>
                  <br />
                </P1>
                <br />
                <br />
                <a name="cross_border_data">
                  <StyledH3>J. Cross-Border Data Transfer</StyledH3>
                </a>
                <P1>
                  Your information including any Personal Information is stored,
                  processed, and transferred in and to the Amazon Web Service
                  (AWS) servers and databases located in Singapore, India and
                  the USA. Unacademy may also store, process, and transfer
                  information in and to servers in other countries depending on
                  the location of its affiliates and service providers.
                </P1>
                <br />
                <P1>
                  Please note that these countries may have differing (and
                  potentially less stringent) privacy laws and that Personal
                  Information can become subject to the laws and disclosure
                  requirements of such countries, including disclosure to
                  governmental bodies, regulatory agencies, and private persons,
                  as a result of applicable governmental or regulatory inquiry,
                  court order or other similar process.
                </P1>
                <br />
                <P1>
                  If you use our Platform from outside India, including in the
                  USA, EU, EEA, and UK, your information may be transferred to,
                  stored, and processed in India. By accessing our Platform or
                  otherwise giving us information, you consent to the transfer
                  of information to India and other countries outside your
                  country of residence. If you are located in the EU and
                  applicable law specifies relevant legal grounds for processing
                  personal data, the legal grounds for our processing activities
                  are to perform our contract(s) with you; to meet our legal
                  obligations; and for our legitimate business purposes,
                  including to improve our operation and Services and to detect
                  and prevent fraud.
                </P1>
                <br />
                <P1>
                  We rely on legal bases to transfer information outside the EU,
                  EEA and UK, and any Personal Information that we transfer will
                  be protected in accordance with this Policy as well as with
                  adequate protections in place in compliance with applicable
                  Data Protection Laws and regulations.
                </P1>
                <br />
                <br />
                <a name="duration_info">
                  <StyledH3>
                    K. Duration for which your Information is Stored
                  </StyledH3>
                </a>
                <P1>
                  Mostly, when you delete any of the information provided by you
                  or when you delete your account, on the Platform, the same
                  will be deleted from our servers too. However, in certain
                  cases, we will retain your information for as long as it is
                  required for us to retain for the purposes stated hereinabove,
                  including for the purpose of complying with legal obligation
                  or business compliances.
                </P1>
                <br />
                <P1>
                  Further, please note that we may not be able to delete all
                  communications or photos, files, or other documents publicly
                  made available by you on the Platform (for example: comments,
                  feedback, etc.), however, we shall anonymize your Personal
                  Information in such a way that you can no longer be identified
                  as an individual in association with such information made
                  available by you on the Platform. We will never disclose
                  aggregated or de-identified information in a manner that could
                  identify you as an individual.
                </P1>
                <br />
                <P1>
                  Note: If you wish to exercise any of your rights (as specified
                  in <b>‘Your Rights’</b> section below) to access, modify and
                  delete any or all information stored about you, then you may
                  do so by using the options provided within the Platform. You
                  can always write to us at
                  <StyledAnchor href="mailto:privacy@unacademy.com">
                    privacy@unacademy.com
                  </StyledAnchor>{' '}
                  for any clarifications needed.
                </P1>
                <br />
                <br />
                <a name="your_choice">
                  <StyledH3>L. Your Choices</StyledH3>
                </a>
                <P1>
                  <li>
                    <b>Limit the information You provide:</b> You always have an
                    option to choose the information you provide to us,
                    including the option to update or delete your information.
                    However, please note that lack of certain information may
                    not allow you the access to the Platform or any of its
                    features, in part or in full.
                  </li>
                  <li>
                    <b>Limit the communications You receive from us: </b>{' '}
                    Further, you will also have the option to choose what kind
                    of communication you would like to receive from us and
                    whether you would like to receive such communication at all
                    or not. However, there may be certain communications that
                    are required for legal or security purposes, including
                    changes to various legal agreements, that you may not be
                    able to limit.
                  </li>
                  <li>
                    <b>Reject Cookies and other similar technologies:</b> You
                    may reject or remove cookies from your web browser; you will
                    always have the option to change the default settings on
                    your web browser if the same is set to ‘accept cookies’.
                    However, please note that some Services offered on the
                    Platform may not function or be available to you, when the
                    cookies are rejected, removed, or disabled.
                  </li>
                </P1>
                <br />
                <br />
                <a name="your_rights">
                  <StyledH3>M. Your Rights</StyledH3>
                </a>
                <P1>
                  In general, all Users have the rights specified herein this
                  section. However, depending on where you are situated, you may
                  have certain specific rights in respect of your Personal
                  Information accorded by the laws of the country you are
                  situated in. To understand{' '}
                  <StyledLink href="#your_rights" as="#your_rights">
                    <b>Your rights</b>
                  </StyledLink>
                  , please refer to the{' '}
                  <StyledLink
                    href="#country_specific_privacy_terms"
                    as="#country_specific_privacy_terms"
                  >
                    Country Specific Additional Terms
                  </StyledLink>{' '}
                  below.
                  <br />
                  <br />
                  If you are a User, you may exercise any of these rights by
                  using the options provided to you within the Platform upon
                  your login. If however, you are facing any issues or require
                  any clarifications, you can always write to us at the address
                  noted in the{' '}
                  <StyledLink href="#grievances" as="#grievances">
                    <b>‘Grievances’</b>
                  </StyledLink>{' '}
                  section below, and we will address your concerns to the extent
                  required by the applicable law.
                </P1>
                <br />
                <br />
                <P1>
                  <ol type="a">
                    <li>
                      <b>Right to Confirmation and Access: </b>You have the
                      right to get confirmation and access to your Personal
                      Information that is with us along with other supporting
                      information.
                    </li>
                    <li>
                      <b>Right to Correction: </b>You have the right to ask us
                      to rectify your Personal Information that is with us that
                      you think is inaccurate. You also have the right to ask us
                      to update your Personal Information that you think is
                      incomplete or out-of-date.
                    </li>
                    <li>
                      <b>Right to be Forgotten: </b>You have the right to
                      restrict or prevent the continuing disclosure of your
                      Personal Information under certain circumstances.
                    </li>
                    <li>
                      <b>Right to Erasure: </b>If you wish to withdraw/remove
                      your Personal Information from our Platform, you have the
                      right to request erasure of your Personal Information from
                      our Platform. However, please note that such erasure will
                      remove all your Personal Information from our Platform
                      (except as specifically stated in this Policy) and may
                      result in deletion of your account on the Platform
                      permanently, and the same will not be retrievable.
                    </li>
                  </ol>
                  <br />
                  Remember, you are entitled to exercise your rights as stated
                  above only with respect to your information, including
                  Personal Information, and not that of other Users. Further,
                  when we receive any requests or queries over email or
                  physically to the address specified in the{' '}
                  <StyledLink href="#grievances" as="#grievances">
                    <b>‘Grievances’</b>
                  </StyledLink>{' '}
                  section below, then, as per the applicable Data Protection
                  Laws, we may need to ask you a few additional information to
                  verify your identity in association with the Platform and the
                  request received.
                </P1>
                <br />
                <br />
                <a name="info_sec">
                  <StyledH3>N. Information Security</StyledH3>
                </a>
                <P1>
                  <li>
                    We work to protect the security of your information during
                    transmission by using Transport Layer Security (TLS) or
                    Secure Sockets Layer (SSL) software (depending on your
                    browser/Supported Device), which encrypts information you
                    input in addition to maintaining security of your
                    information as required under applicable laws.
                  </li>
                  <li>
                    We maintain electronic, and procedural safeguards in
                    connection with the collection, storage, and disclosure of
                    Personal Information (including Sensitive Personal
                    Information). Our security procedures mean that we may
                    occasionally request proof of identity before we disclose
                    Personal Information to you that belongs to you.
                  </li>
                  <li>
                    However, no form or method of data storage or transmission
                    system is fully secure, and we cannot guarantee that
                    security provided by such system(s) is absolute and that
                    your information will not be accessed, disclosed, or
                    destroyed in the event of a breach of any of our security
                    measures.
                  </li>
                  <li>
                    It is important for you to protect your account against
                    unauthorized access to or use of your password and to your
                    computer and if you have any reason to believe that your
                    password has become known to anyone else, or if the password
                    is being, or is likely to be, used in an unauthorized
                    manner, you must immediately change your password or inform
                    us, so that we are able to help you stop or prevent such
                    unauthorized access. Be sure to sign off when you finish
                    using a shared computer.
                  </li>
                  <li>
                    All KYC information collected in accordance with this Policy
                    are fully encrypted and cannot be accessed by any person
                    other than the designated authority in Unacademy.
                  </li>
                  <li>
                    We try and ensure that the third parties who provide
                    services to us under appropriate contracts take appropriate
                    security measures to protect Personal Information in line
                    with our policies.
                  </li>
                </P1>
                <br />
                <br />
                <a name="personal_comm">
                  <StyledH3>O. Promotional Communications</StyledH3>
                </a>
                <P1>
                  You will always have the option to opt out of receiving some
                  or all of our promotional communications through the setting
                  provided within the Platform upon your login, by using the
                  unsubscribe link in any email communications sent to you or by
                  emailing{' '}
                  <StyledAnchor href="mailto:help@unacademy.com">
                    help@unacademy.com
                  </StyledAnchor>
                  .
                </P1>
                <br />
                <P1>
                  If you opt out of promotional communications, we may still
                  send you transactional communications, such as service
                  announcements, administrative and legal notices, and
                  information about your account, without offering you the
                  opportunity to opt out of these communications. If you no
                  longer wish to use our Platform or receive any communications
                  from us (except for those that are legally required), then you
                  may delete your account by using the option enabled within the
                  Platform or by writing to us at{' '}
                  <StyledAnchor href="mailto:privacy@unacademy.com">
                    privacy@unacademy.com
                  </StyledAnchor>
                  .
                </P1>
                <br />
                <P1>
                  Advertisers or ad companies working on their behalf sometimes
                  use technology to serve the ads that appear on our sites
                  directly to Your browser. They automatically receive Your IP
                  address when this happens. They may also use cookies to
                  measure the effectiveness of their ads and to personalize ad
                  content. We do not have access to or control over cookies or
                  other features that advertisers and third-party sites may use,
                  and the information practices of these advertisers and
                  third-party websites are not covered by our Policy. Please
                  contact them directly for more information about their privacy
                  practices.
                </P1>
                <br />
                <P1>
                  Please note that opting out of promotional email
                  communications only affects future communications from us. If
                  we have already provided your information to a third party (as
                  stated in this Policy) before you changed your preferences or
                  updated your information, you may have to change your
                  preferences directly with that third party.
                </P1>
                <br />
                <P1>
                  We do not sell your Personal Information to third parties, and
                  we do not use your name or name of your company in marketing
                  statements without your consent.
                </P1>
                <br />
                <br />
                <a name="interest_based_ads">
                  <StyledH3>P. Interest-Based Ads</StyledH3>
                </a>
                <P1>
                  On unaffiliated sites, Unacademy may display interest-based
                  advertising using information you make available to us when
                  you interact with our Platform and Services. Interest-based
                  ads, also sometimes referred to as personalised or targeted
                  ads, are displayed to you based on information from activities
                  such as registering with our Platform, visiting sites that
                  contain Unacademy content or ads. In providing interest-based
                  ads, we follow applicable laws, as well as the Code for
                  Self-Regulation in Advertising by the Advertising Standards
                  Council of India and the Self-Regulatory Principles for Online
                  Behavioural Advertising developed by the Digital Advertising
                  Alliance (a coalition of marketing, online advertising, and
                  consumer advocacy organizations) and other rules and
                  guidelines issued by the Federal Trade Commission in respect
                  of digital advertising.
                </P1>
                <br />
                <P1>
                  We do not provide any Personal Information to advertisers or
                  to third party sites that display our interest-based ads.
                  However, advertisers and other third-parties (including the ad
                  networks, ad-serving companies, and other service providers
                  they may use) may assume that users who interact with or click
                  on a personalised ad or content are part of the group that the
                  ad or content is directed towards. Also, some third-parties
                  may provide us information about you (such as the sites where
                  you have been shown ads or demographic information) from
                  offline and online sources that we may use to provide you more
                  relevant and useful advertising.
                </P1>
                <br />
                <P1>
                  Advertisers or ad companies working on their behalf sometimes
                  use technology to serve the ads that appear on our sites
                  directly to your browser. They automatically receive your IP
                  address when this happens. They may also use cookies to
                  measure the effectiveness of their ads and to personalise ad
                  content. We do not have access to or control over cookies or
                  other features that advertisers and third-party sites may use,
                  and the information practices of these advertisers and
                  third-party websites are not covered by our Policy. Please
                  contact them directly for more information about their privacy
                  practices.
                </P1>
                <br />
                <br />
                <a name="mod-privacy-policy">
                  <StyledH3>Q. Modification to Privacy Policy</StyledH3>
                </a>
                <P1>
                  Our business changes constantly and our Policy may change from
                  time to time. We may, at our discretion (unless required by
                  applicable laws to mandatorily do so), email periodic
                  reminders of our notices and conditions, unless you have
                  instructed us not to, but we encourage you to check our
                  Platform frequently to see the recent changes. Unless stated
                  otherwise, our current Policy applies to all information that
                  we have about you and your account. We stand behind the
                  promises we make, however, and will not materially change our
                  policies and practices making them less protective of customer
                  information collected in the past without your consent.
                </P1>
                <br />
                <br />
                <a name="grievances">
                  <StyledH3>R. Privacy Grievances</StyledH3>
                </a>
                <P1>
                  If you have any questions about this Policy, wish to exercise
                  your rights, have concerns about privacy of your data or any
                  privacy related grievances in respect of the Platform, then
                  please register your complaint with a thorough description via
                  email to{' '}
                  <StyledAnchor href="mailto:privacy@unacademy.com">
                    privacy@unacademy.com
                  </StyledAnchor>{' '}
                  addressed to our grievance officer Mr. Tony Mathew (Associate
                  General Counsel) or via a registered post to the below
                  address-
                  <br />
                  <br />
                  <b>Sorting Hat Technologies Pvt Ltd</b>
                  <br />
                  Maruti Infotech Centre, 3rd Floor,
                  <br /> A-Block, Domlur, Koramangala Inner Ring Road,
                  <br /> Bangalore- 560 071, Karnataka, India.
                </P1>
                <br />
                <br />
                <a name="country_specific_privacy_terms">
                  <StyledH3>
                    S. Country Specific Additional Privacy Terms
                  </StyledH3>
                </a>
                <P1>
                  <ol type="A">
                    <li>
                      <b>TERMS APPLICABLE IF YOU ARE AN INDIAN RESIDENT</b>
                      <br />
                      <br />
                      <StyledLink href="#your_rights" as="#your_rights">
                        <b>Your rights: </b>
                      </StyledLink>
                      If you are located in India, you may have the following
                      rights under the Personal Data Protection Bill (PDPB) when
                      it becomes a legislation. All requests can be made by
                      using the option provided to you within the Platform upon
                      your login. You may also write to us as stated in the{' '}
                      <StyledLink href="#grievances" as="#grievances">
                        ‘Grievances’
                      </StyledLink>{' '}
                      section above, and we will address you concerns to the
                      extent required by law.
                    </li>
                    <br />
                    <ol type="1">
                      <li>
                        <b>Right to Confirmation and Access:</b> You have the
                        right to get confirmation and access to your Personal
                        Information that is with us along with other supporting
                        information.
                      </li>
                      <li>
                        <b>Right to Correction:</b> You have the right to ask us
                        to rectify your Personal Information that is with us
                        that you think is inaccurate. You also have the right to
                        ask us to update your Personal Information that you
                        think is incomplete or out-of-date.
                      </li>
                      <li>
                        <b>Right to Data Portability:</b> You have the right to
                        ask that we transfer the Personal Information you gave
                        us to another organisation, or to you, under certain
                        circumstances.
                      </li>
                      <li>
                        <b>Right to be Forgotten:</b> You have the right to
                        restrict or prevent the continuing disclosure of your
                        Personal Information under certain circumstances.
                      </li>
                      <li>
                        <b>Right to Erasure: </b> f you wish to withdraw/remove
                        your Personal Information from our Platform, you have
                        the right to request erasure of your Personal
                        Information from our Platform. However, please note that
                        such erasure will remove all your Personal Information
                        from our Platform (except as specifically stated in this
                        Policy) and may result in deletion of your account on
                        the Platform permanently, and the same will not be
                        retrievable.
                      </li>
                    </ol>
                    <br />
                    <li>
                      <b>
                        TERMS APPLICABLE IF YOU ARE A RESIDENT OF UNITED KINGDOM
                        (UK), A EUROPEAN UNION (EU) COUNTRY OR EUROPEAN ECONOMIC
                        AREA (EEA)
                      </b>
                      <br />
                      <br />
                      <StyledLink href="#your_rights" as="#your_rights">
                        <b>Your rights: </b>
                      </StyledLink>
                      If you are located in the United Kingdom (UK) or European
                      Union (EU) or European Economic Area (EEA), you have the
                      following rights under the UK and EU General Data
                      Protection Regulation (GDPR) respectively. All requests
                      should be sent to the address noted in the{' '}
                      <StyledLink href="#grievances" as="#grievances">
                        ‘Grievances’
                      </StyledLink>{' '}
                      section above, and we will fulfil requests to the extent
                      required by applicable law.
                    </li>
                    <br />
                    <ol type="1">
                      <li>
                        <b>Right to access Your Personal Information:</b> You
                        have the right to receive confirmation as to whether or
                        not Personal Information concerning you is being
                        processed and, where that is the case, access to the
                        Personal Information can be sought;
                      </li>
                      <li>
                        <b>Right to Rectification:</b> Our goal is to keep your
                        Personal Information accurate, current and complete.
                        Please contact us if you believe your information is
                        inaccurate or incomplete;
                      </li>
                      <li>
                        <b>Right to Erasure: </b> In some cases, you have a
                        legal right to request that we erase your Personal
                        Information;
                      </li>
                      <li>
                        <b>Right to object to Processing:</b> You have the right
                        to object to our processing of your Personal Information
                        under certain conditions;
                      </li>
                      <li>
                        <b>Right to restrict Processing: </b> You have the right
                        to request that we restrict the processing of your
                        Personal Information, under certain conditions;
                      </li>
                      <li>
                        <b>Right to Data Portability: </b> You have the right to
                        request that we transfer the data that we have collected
                        to another organization, or directly to you, under
                        certain conditions;
                      </li>
                      <li>
                        <b>
                          Right to make a complaint to a government supervisory
                          authority:{' '}
                        </b>{' '}
                        If you believe we have not processed your Personal
                        Information in accordance with applicable provisions of
                        the GDPR, we encourage you to contact us at
                        <StyledAnchor href="mailto:privacy@unacademy.com">
                          privacy@unacademy.com
                        </StyledAnchor>
                        . You also have the right to make a GDPR complaint to
                        the relevant Supervisory Authority or seek a remedy
                        through the courts. A list of Supervisory Authorities is
                        available at:
                        <StyledAnchor
                          href="https://edpb.europa.eu/about-edpb/board/members_en"
                          target="_blank"
                        >
                          https://edpb.europa.eu/about-edpb/board/members_en
                        </StyledAnchor>
                        . If you need further assistance regarding your rights,
                        please contact us using the contact information provided
                        below, and we will consider your request in accordance
                        with applicable law. You can identify the supervising
                        authority of your concern by visiting
                        <StyledAnchor
                          href="https://edpb.europa.eu/about-edpb/board/members_en"
                          target="_blank"
                        >
                          https://edpb.europa.eu/about-edpb/board/members_en
                        </StyledAnchor>
                        .
                      </li>
                      <li>
                        <b>
                          Right to not be subject to automated decision-making,
                          including profiling:{' '}
                        </b>{' '}
                        You have the right not to be subject to a decision based
                        solely on automated processing, including profiling,
                        which produces legal or similarly significant effects
                        concerning you.
                      </li>
                    </ol>
                    <br />
                    We collect and process Personal Information about you only
                    where we have a legal rationale to do so. Specific legal
                    rationale applied for the same will depend on the type of
                    Personal Information collected and the context in which the
                    same is being processed, including the Services involved.
                    <br />
                    <br />
                    <li>
                      <b>
                        TERMS APPLICABLE IF YOU ARE A CALIFORNIA STATE RESIDENT
                      </b>
                      <br />
                      <br />
                      If you are a California state resident, then you have the
                      following rights to the extent, and in the manner, set out
                      in the CCPA:
                    </li>
                    <br />
                    <ol type="1">
                      <li>
                        The right to access the Personal Information that we
                        hold on you;
                      </li>
                      <li>
                        The right to know what Personal Information we intend on
                        collecting from them before the point of collection;
                      </li>
                      <li>
                        The right to opt in or out of marketing, analytics, and
                        other similar activities;
                      </li>
                      <li>
                        The right to equal services without discrimination; and
                      </li>
                      <li>
                        The right to request deletion of Personal Information.
                      </li>
                    </ol>
                    <br />
                    The above rights, the manner in which you can exercise the
                    same and the category of and the manner in which we collect
                    your information, are detailed below.
                  </ol>
                </P1>
                <br />
                <br />
                <a name="ccpa_notice_collection">
                  <StyledH3>T. CCPA NOTICE AT COLLECTION</StyledH3>
                </a>
                <P1>
                  For purposes of the CCPA, in collecting the information
                  described above, we collect the categories of Personal
                  Information listed below from you:
                  <ol type="a">
                    <li>
                      <b>Identifiers:</b>We may collect your name, email
                      address, mobile number, username, unique personal
                      identifier, and Internet Protocol (IP) address. We use
                      Identifiers as set forth in the “How We Use and Share the
                      Information Collected” section of this Policy, like your
                      name, contact information, and device and online
                      identifiers.
                    </li>
                    <li>
                      <b>
                        Characteristics of Personal Information described in the
                        California Customer Records statute:
                      </b>
                      We may collect your name, email address, username, unique
                      personal identifier, and gender. We use Categories of
                      Personal Information described in the California Consumer
                      Records statute as set forth in the
                      <StyledLink href="#use_share_info" as="#use_share_info">
                        <b> ‘How We Use and Share the Information Collected’</b>
                      </StyledLink>{' '}
                      section of this Policy.
                    </li>
                    <li>
                      <b>
                        Internet or other electronic network activity
                        information:
                      </b>
                      We collect cookies as described in our{' '}
                      <StyledLink href="/cookie-policy" as="/cookie-policy">
                        Cookie Policy
                      </StyledLink>
                      , we will automatically receive information from your
                      browser and your device, which includes the date and time
                      of your visit to the Platform as well as your location,
                      Internet Protocol (IP) address, domain server, browser
                      type, access time, and data about which pages you visit on
                      the Platform. We use Internet or other electronic network
                      activity information as set forth in the{' '}
                      <StyledLink href="#use_share_info" as="#use_share_info">
                        <b> ‘How We Use and Share the Information Collected’</b>
                      </StyledLink>{' '}
                      section of this Policy.
                    </li>
                    <li>
                      <b>Geolocation data:</b>We may collect your IP address. We
                      use Geolocation Data as set forth in the{' '}
                      <StyledLink href="#use_share_info" as="#use_share_info">
                        <b> ‘How We Use and Share the Information Collected’</b>
                      </StyledLink>{' '}
                      section of this Policy. (such as your location based on
                      your IP address);
                    </li>
                    <li>
                      <b>Audio, electronic, visual or similar information:</b>{' '}
                      We may collect your profile picture or other audio or
                      visual information uploaded as content to the Platform. We
                      use audio, electronic, visual or similar information as
                      set forth in the{' '}
                      <StyledLink href="#use_share_info" as="#use_share_info">
                        <b> ‘How We Use and Share the Information Collected’</b>
                      </StyledLink>{' '}
                      section of this Policy.
                    </li>
                    <li>
                      <b>Inferences:</b>We may make inferences based upon the
                      Personal Information collected (such as likelihood of
                      retention or attrition). We use Inference information as
                      set forth in the{' '}
                      <StyledLink href="#use_share_info" as="#use_share_info">
                        <b> ‘How We Use and Share the Information Collected’</b>
                      </StyledLink>{' '}
                      section of this Policy.
                    </li>
                  </ol>
                </P1>
                <br />
                <br />
                <a name="ccpa_data_practices">
                  <StyledH3>
                    U. CCPA DATA PRACTICES DURING THE LAST 12 MONTHS
                  </StyledH3>
                </a>
                <P1>
                  <ol type="a">
                    <li>
                      <b>
                        <StyledLink
                          href="#personal_information"
                          as="#personal_information"
                        >
                          <b>Personal Information collected: </b>
                        </StyledLink>
                      </b>{' '}
                      As described in this Policy, we have collected the
                      categories of Personal Information listed below during the
                      preceding 12 months:
                      <ul>
                        <li>Identifiers</li>
                        <li>
                          Characteristics of Personal Information described in
                          the California Customer Records statute
                        </li>
                        <li>
                          Internet or other electronic network activity
                          information
                        </li>
                        <li>Geolocation data</li>
                        <li>Commercial information</li>
                        <li>
                          Audio, electronic, visual, thermal, olfactory, or
                          similar information
                        </li>
                        <li>Inferences</li>
                      </ul>
                    </li>
                    <br />
                    <li>
                      <b>Categories of sources:</b> We have collected the
                      Personal Information identified in this Policy from you
                      and our payment processors.
                    </li>
                    <br />
                    <li>
                      <b>Business and commercial purposes for collecting</b> We
                      have collected the categories of Personal Information
                      listed above for the following purposes:
                      <ul>
                        <li>Operate the Platform;</li>
                        <li>Provide our Services to you;</li>
                        <li>Honor our Terms and Conditions and contracts;</li>
                        <li>
                          Ensure the privacy and security of our Platform and
                          Services;
                        </li>
                        <li>Manage our relationships with you;</li>
                        <li>Communicate with you;</li>
                        <li>Analyze use of the Platform and our Services;</li>
                        <li>Enhance your experience;</li>
                        <li>Track visits to the Platform;</li>
                        <li>
                          Provide you with a more personal and interactive
                          experience on the Platform; and
                        </li>
                        <li>Usage analytics purposes.</li>
                      </ul>
                    </li>
                    <br />
                    <li>
                      <b>Personal Information sold:</b> We have not sold
                      categories of Personal Information during the preceding 12
                      months.
                    </li>
                    <br />
                    <li>
                      <b>
                        Personal Information disclosed for a business purpose:
                      </b>{' '}
                      We have disclosed for a business purpose the categories of
                      Personal Information listed below during the preceding 12
                      months:
                      <ul>
                        <li>Identifiers</li>
                        <li>
                          Characteristics of Personal Information described in
                          the California Customer Records statute
                        </li>
                        <li>
                          Internet or other electronic network activity
                          information
                        </li>
                        <li>Geolocation data</li>
                        <li>Commercial information</li>
                        <li>
                          Audio, electronic, visual, thermal, olfactory, or
                          similar information
                        </li>
                        <li>Inferences</li>
                      </ul>
                    </li>
                    <br />
                    We have disclosed each category of Personal Information to
                    the following categories of third parties: (1) corporate
                    parents, subsidiaries, and affiliates; (2) advisors
                    (accountants, attorneys); (3) service providers (data
                    analytics, data storage, mailing, marketing, website and
                    platform administration, technical support); and (4)
                    operating systems and platforms.
                  </ol>
                </P1>
                <br />
                <br />
                <a name="consumer_rights_under_ccpa">
                  <StyledH3>
                    V. CONSUMER RIGHTS AND REQUESTS UNDER THE CCPA
                  </StyledH3>
                </a>
                <P1>
                  The CCPA gives consumers the right to request that we (1)
                  disclose what Personal Information we collect, use, disclose,
                  and sell, and (2) delete certain Personal Information that we
                  have collected or maintained. you may submit these requests to
                  us as described below, and we honor these rights where they
                  apply.
                </P1>
                <br />
                <P1>
                  If a request is submitted in a manner that is not one of the
                  designated methods for submission, or if the request is
                  deficient in some manner unrelated to our verification
                  process, we will either (i) treat the request as if it had
                  been submitted in accordance with the designated manner, or
                  (ii) provide you with specific directions on how to submit the
                  request or remedy any deficiencies with the request, as
                  applicable.
                </P1>
                <br />
                <P1>
                  <ol type="a">
                    <li>
                      <b>Request to Know:</b> As a California resident, you have
                      the right to request: (1) the specific pieces of Personal
                      Information we have collected about you; (2) the
                      categories of Personal Information we have collected about
                      you; (3) the categories of sources from which the Personal
                      Information is collected; (4) the categories of Personal
                      Information about you that we have sold and the categories
                      of third parties to whom the Personal Information was
                      sold; (5) the categories of Personal Information about you
                      that we disclosed for a business purpose and the
                      categories of third parties to whom the Personal
                      Information was disclosed for a business purpose; (6) the
                      business or commercial purpose for collecting, disclosing,
                      or selling Personal Information; and (7) the categories of
                      third parties with whom we share Personal Information. Our
                      response will cover the 12-month period preceding our
                      receipt of a verifiable request.
                    </li>
                    <br />
                    <li>
                      <b>Request to Delete:</b> As a California resident, you
                      have a right to request the erasure/deletion of certain
                      Personal Information collected or maintained by us. As
                      described herein, we will delete your Personal Information
                      from our records and direct any service providers (as
                      defined under applicable law) to delete your Personal
                      Information from their records. However, we are not
                      required to honor a deletion request if an exemption
                      applies under the law.
                    </li>
                    <br />
                    <li>
                      <b>Submitting a Request:</b>
                      <ol type="1">
                        <li>
                          <i>Submission of Instructions: </i>You may submit a
                          request to know or to delete by email to{' '}
                          <StyledAnchor href="mailto:privacy@unacademy.com">
                            privacy@unacademy.com
                          </StyledAnchor>{' '}
                          or by submitting a request via mail to Unacademy Inc.,
                          Vistra (Delaware) Ltd, 3500 South Dupont Hwy, Dover,
                          Kent, DE 19901. Regarding requests to delete, we may
                          present you with the choice to delete select portions
                          of your Personal Information, but a global option to
                          delete all Personal Information will be offered and
                          more prominently presented.
                        </li>
                        <br />
                        <li>
                          <i>Verification Process: </i>We are required by law to
                          verify the identities of those who submit requests to
                          know or to delete. To determine whether the individual
                          making the request is the consumer about whom we have
                          collected information, we will verify your identity by
                          matching the identifying information provided by you
                          in the request to the Personal Information that we
                          already maintain about you. As a part of this process,
                          you will be required to provide your name and email
                          address, and mobile number (if the same have been
                          voluntarily provided by you at the time of registering
                          on the Platform or otherwise while using the
                          Platform). We will inform you if we cannot verify your
                          identity. Please note-
                          <br />
                          <br />
                          <ol type="a">
                            <li>
                              If we cannot verify the identity of the person
                              making a request for categories of Personal
                              Information, we may deny the request. If the
                              request is denied in whole or in part for this
                              reason, we will provide a copy of, or direct you
                              to, our Privacy Policy.
                            </li>
                            <li>
                              If we cannot verify the identity of the person
                              making a request to delete, we may deny the
                              request. If there is no reasonable method by which
                              we can verify the identity of the requestor to the
                              degree of certainty required, we will state this
                              in our response and explain why we have no
                              reasonable method by which we can verify the
                              identity of the requestor.
                            </li>
                            <li>
                              If we cannot verify the identity of the person
                              making a request for categories of Personal
                              Information, we may deny the request. If the
                              request is denied in whole or in part for this
                              reason, we will provide a copy of, or direct you
                              to, our Privacy Policy.
                            </li>
                          </ol>
                        </li>
                        <br />
                        <li>
                          <i>Authorized Agents: </i> Authorized agents may
                          submit requests via the methods identified in this
                          Policy. If you use an authorized agent to submit a
                          request to know or a request to delete, we may require
                          you to: (1) provide the authorized agent with signed
                          permission to do so; (2) verify your identity directly
                          with us; and (3) directly confirm with us that you
                          provided the authorized agent permission to submit the
                          request. However, we will not require these actions if
                          you have provided the authorized agent with power of
                          attorney pursuant to the California Probate Code.
                        </li>
                        <br />
                        <li>
                          <i>Excessive Requests:</i> If requests from a User are
                          manifestly unfounded or excessive, in particular
                          because of their repetitive character, we may either
                          (1) charge a reasonable fee, or (2) refuse to act on
                          the request and notify the User of the reason for
                          refusing the request. If we charge a fee, the amount
                          will be based upon the administrative costs of
                          providing the information or communication or taking
                          the action requested.
                        </li>
                        <br />
                        <li>
                          <i>CCPA Non-Discrimination:</i> You have the right not
                          to receive discriminatory treatment by us due to your
                          exercise of the rights provided by the CCPA. We do not
                          offer financial incentives and price or service
                          differences, and we do not discriminate against
                          Users/consumers for exercising their rights under the
                          CCPA.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </P1>
                <br />
                <br />
                <P1>
                  <i>
                    Privacy Policy – Version 2.1
                    <br /> Last Updated on 17th March 2022
                  </i>
                </P1>
              </article>
            </div>
            <br />
            <br />
          </div>
        </PrivacyWrapper>
      </RightContainer>
    </>
  );
};

Privacy.pageInfo = {
  name: 'Unacademy Privacy',
  isMWebSupported: true,
  layoutOptions: {
    hideGoalSelector: true,
    disableDarkMode: true
  }
};

export default Privacy;
Privacy.Layout = Layout;
Privacy.Wrapper = PageReduxProvider;
